import React from "react";
import { HandCheck, Scribble } from "../brand/Doodles";

export const Section = ({ eyebrow, title, children, testId, id, className = "" }) => (
  <section id={id} className={`max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20 ${className}`} data-testid={testId}>
    {eyebrow && <div className="ggm-eyebrow mb-3">{eyebrow}</div>}
    {title && (
      <h2
        className="font-condensed uppercase text-white leading-[1.05] max-w-4xl"
        style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)", letterSpacing: "-0.015em" }}
      >
        {title}
      </h2>
    )}
    <div className={title ? "mt-8" : ""}>{children}</div>
  </section>
);

export const Card = ({ children, className = "", testId }) => (
  <div
    className={`rounded-md p-7 ${className}`}
    style={{ background: "var(--ggm-surface)", borderLeft: "3px solid rgba(40,200,240,0.55)", boxShadow: "0 14px 30px -22px rgba(0,0,0,0.8)" }}
    data-testid={testId}
  >
    {children}
  </div>
);

export const CheckList = ({ items, testId }) => (
  <ul className="space-y-3" data-testid={testId}>
    {items.map((it, i) => (
      <li key={i} className="flex gap-3 font-body text-white/80 text-base leading-relaxed">
        <span className="mt-0.5 text-white/50"><HandCheck color="var(--ggm-signal-cyan)" size={22} /></span>
        <span>
          {Array.isArray(it) ? (
            <>
              <b className="text-white">{it[0]}</b> {it[1]}
            </>
          ) : (
            it
          )}
        </span>
      </li>
    ))}
  </ul>
);

export const Badges = ({ items, testId }) => (
  <ul className="mt-6 flex flex-wrap gap-x-7 gap-y-2" data-testid={testId}>
    {items.map((b) => (
      <li key={b} className="inline-flex items-center gap-2 ggm-hand text-base">
        <span className="text-white/40"><HandCheck color="var(--ggm-signal-cyan)" size={20} /></span> {b}
      </li>
    ))}
  </ul>
);

// Notebook-page checklist (light paper, navy ink)
export const Steps = ({ steps, testId, renderExtra }) => (
  <ol
    className="ggm-ruled rounded-sm px-5 md:px-8 py-2 max-w-4xl md:-rotate-[0.3deg]"
    style={{ background: "#FFFFFF", borderLeft: "3px solid rgba(11,60,220,0.35)", boxShadow: "0 18px 40px -22px rgba(0,0,0,0.7)" }}
    data-testid={testId}
  >
    {steps.map((s, i) => (
      <li key={i} className="flex gap-4 py-5" style={{ borderBottom: i < steps.length - 1 ? "1px dashed rgba(11,60,220,0.22)" : "none" }} data-testid={`${testId}-${i + 1}`}>
        <span className="ggm-hand text-2xl shrink-0 w-10" style={{ color: "var(--ggm-orbit-blue)" }}>0{i + 1}.</span>
        <div className="font-body leading-relaxed" style={{ color: "rgba(11,30,58,0.8)" }}>
          <b style={{ color: "var(--ggm-navy)" }}>{s[0]}</b> {s[1]}
          {renderExtra && <div className="[&_a]:!text-[var(--ggm-orbit-blue)]">{renderExtra(s)}</div>}
        </div>
      </li>
    ))}
  </ol>
);

export const FaqList = ({ faqs, testId }) => (
  <dl className="max-w-3xl" data-testid={testId}>
    {faqs.map((f, i) => (
      <div key={f.q} className="py-5" style={{ borderTop: i ? "1px dashed rgba(255,255,255,0.16)" : "none" }} data-testid={`${testId}-item-${i + 1}`}>
        <dt className="font-condensed uppercase text-white" style={{ fontSize: "1.1rem" }}>
          <span className="ggm-hand normal-case mr-2">Q.</span>
          {f.q}
        </dt>
        <dd className="mt-2 font-body text-white/75 text-base leading-relaxed">{f.a}</dd>
      </div>
    ))}
  </dl>
);

export const PageHero = ({ eyebrow, h1, lede, badges, children, testId }) => (
  <section
    className="relative max-w-6xl mx-auto px-5 md:px-8"
    style={{ paddingTop: "clamp(48px, 8vh, 96px)", paddingBottom: "clamp(24px, 4vh, 48px)" }}
    data-testid={testId}
  >
    <div className="ggm-eyebrow mb-5">{eyebrow}</div>
    <h1
      className="font-condensed uppercase text-white leading-[1.02] max-w-5xl"
      style={{ fontSize: "clamp(2.1rem, 5.4vw, 4.25rem)", letterSpacing: "-0.015em" }}
      data-testid={`${testId}-h1`}
    >
      {h1}
    </h1>
    <Scribble className="mt-3 w-40 md:w-56 h-4 block" />
    <p className="mt-6 font-body text-white/80 text-base md:text-lg leading-relaxed max-w-3xl">{lede}</p>
    {badges?.length > 0 && <Badges items={badges} testId={`${testId}-badges`} />}
    <div className="mt-9 flex flex-wrap gap-4 items-center">{children}</div>
  </section>
);
